// arsenal-tpms/frontend/src/components/vehicles/VehicleDetail.jsx (ACTUALIZADO)
import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  useTheme,
  alpha,
  CircularProgress,
  useMediaQuery,
  Drawer,
  Chip,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  DirectionsCar as VehicleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  SwapHoriz as SwapIcon,
  Sensors as SensorIcon,
  Router as ReceiverIcon,
  Group as OperatorIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useAuth } from '../../hooks/useAuth';
import { useApiCall, useMutation } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useNotification } from '../common/GlobalNotifications';

import VehicleOverviewTab from './detail/VehicleOverviewTab';
import VehicleSensorsTab from './detail/VehicleSensorsTab';
import AlarmProfilesTab from './detail/AlarmProfilesTab';
import VehicleConnectivityTab from './detail/VehicleConnectivityTab';
import VehicleOperatorsPanel from '../operators/VehicleOperatorsPanel';
import SensorAssignmentDialog from './detail/SensorAssignmentDialog';
import SensorRegistrationDialog from './detail/SensorRegistrationDialog';
import { printSection, printTimestamp } from '../../utils/print';
import RotationPreview from './detail/RotationPreview';

// Utility functions
const ensureString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return fallback;
};

// TabPanel Component
const TabPanel = ({ children, value, index, t = (k) => k, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={t('hardcoded.components_vehicles_vehicledetail.vehicle-tabpanel-index', { index: index })}
    aria-labelledby={t('hardcoded.components_vehicles_vehicledetail.vehicle-tab-index', { index: index })}
    {...other}
  >
    {value === index && (
      <Box sx={{
        p: { xs: 2, sm: 3 },
        height: '100%',
        overflow: 'auto'
      }}>
        {children}
      </Box>
    )}
  </div>
);

const VehicleDetail = ({
  open,
  onClose,
  vehicleId,
  vehicle = null,
  onSensorUnassigned = () => { },
  onVehicleUpdated = () => { },
  onVehicleDeleted = () => { },
  allVehicles = [],
  sensors = [],
  vehicleConfigs = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t: originalT, ready: i18nReady } = useTranslation();
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();

  const t = (key, fallback = null, options = {}) => {
    try {
      if (!i18nReady) {
        return fallback || key.split('.').pop() || key;
      }
      const result = originalT(key, options);
      if (result && typeof result === 'object' && typeof result.then === 'function') {
        return fallback || key.split('.').pop() || key;
      }
      if (typeof result === 'string') {
        return result;
      }
      return fallback || key.split('.').pop() || key;
    } catch (error) {
      return fallback || key.split('.').pop() || key;
    }
  };

  // State
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [operatorCount, setOperatorCount] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, sensorId: null });
  const [sensorDialog, setSensorDialog] = useState({ open: false, position: null });
  const [registerSensorDialog, setRegisterSensorDialog] = useState(false);
  const [registeringSensors, setRegisteringSensors] = useState(false);
  const [deleteVehicleDialog, setDeleteVehicleDialog] = useState(false);
  const [rotationMode, setRotationMode] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [rotationConfirmDialog, setRotationConfirmDialog] = useState(false);

  // NUEVO: API call para sensores internos
  const {
    data: internalSensors,
    loading: sensorsLoading,
    refetch: refetchSensors
  } = useApiCall(() => api.sensors.getAll(), []);

  // API calls
  const {
    data: vehicleData,
    loading: vehicleLoading,
    refetch: refetchVehicle
  } = useApiCall(() => vehicleId ? api.vehicles.getById(vehicleId) : null, [vehicleId]);

  // Mutations
  const { mutate: unassignSensor, loading: unassigning } = useMutation(
    (assignmentId) => api.vehicles.removeSensor(vehicleId, assignmentId),
    {
      onSuccess: () => {
        showSuccess(t('vehicleDetail.sensors.unassignSuccess', 'Sensor desasignado exitosamente'));
        refetchVehicle();
        refetchSensors(); // NUEVO: Refrescar sensores
        onSensorUnassigned();
      },
      onError: (error) => showError(ensureString(error.message, t('vehicleDetail.sensors.unassignError', 'Error al desasignar sensor')))
    }
  );

  const { mutate: assignSensor, loading: assigning } = useMutation(
    (data) => api.vehicles.assignSensor(vehicleId, data),
    {
      onSuccess: () => {
        showSuccess(t('vehicles.actions.sensorAssigned', 'Sensor asignado exitosamente'));
        refetchVehicle();
        refetchSensors(); // NUEVO: Refrescar sensores
        onSensorUnassigned();
        setSensorDialog({ open: false, position: null });
      },
      onError: (error) => showError(ensureString(error.message, t('hardcoded.components_vehicles_vehicledetail.error_al_asignar_sensor')))
    }
  );

  const { mutate: updateVehicle, loading: updating } = useMutation(
    (data) => api.vehicles.update(vehicleId, data),
    {
      onSuccess: () => {
        showSuccess(t('vehicles.actions.vehicleUpdated', 'Vehículo actualizado exitosamente'));
        refetchVehicle();
        onVehicleUpdated();
        // NO cerramos editMode aquí para mantenerlo activo
      },
      onError: (error) => showError(ensureString(error.message, t('hardcoded.components_vehicles_vehicledetail.error_al_actualizar_vehiculo')))
    }
  );

  const { mutate: deleteVehicle, loading: deleting } = useMutation(
    () => api.vehicles.delete(vehicleId),
    {
      onSuccess: () => {
        showSuccess(t('vehicles.actions.vehicleDeleted', 'Vehículo eliminado exitosamente'));
        setDeleteVehicleDialog(false);
        onClose();
        onVehicleDeleted();
      },
      onError: (error) => showError(ensureString(error.message, t('hardcoded.components_vehicles_vehicledetail.error_al_eliminar_vehiculo')))
    }
  );

  const currentVehicle = vehicleData || vehicle;

  // NUEVO: Usar sensores internos si están disponibles, sino usar los del prop
  const availableSensorsData = internalSensors || sensors;

  // Form validation
  const vehicleSchema = Yup.object({
    alias: Yup.string().max(100, t('hardcoded.components_vehicles_vehicledetail.el_alias_debe_tener_menos')),
    make: Yup.string().max(50, t('hardcoded.components_vehicles_vehicledetail.la_marca_debe_tener_menos')),
    model: Yup.string().max(50, t('hardcoded.components_vehicles_vehicledetail.el_modelo_debe_tener_menos')),
    type: Yup.string(),
    year: Yup.number()
      .min(1900, t('hardcoded.components_vehicles_vehicledetail.el_ano_debe_ser_posterior'))
      .max(new Date().getFullYear() + 1, t('hardcoded.components_vehicles_vehicledetail.el_ano_no_puede_ser')),
    configuration_id: Yup.number().required(t('hardcoded.components_vehicles_vehicledetail.la_configuracion_es_requerida')),
    parent_vehicle_id: Yup.string().nullable()
  });

  const formik = useFormik({
    initialValues: {
      alias: currentVehicle?.alias || '',
      make: currentVehicle?.make || '',
      model: currentVehicle?.model || '',
      type: currentVehicle?.type || '',
      year: currentVehicle?.year || new Date().getFullYear(),
      configuration_id: currentVehicle?.configuration_id?.toString() || '',
      parent_vehicle_id: currentVehicle?.parent_vehicle_id || '',
      allow_dual_operator: currentVehicle?.allow_dual_operator ?? false
    },
    enableReinitialize: true,
    validationSchema: vehicleSchema,
    onSubmit: (values) => {
      const submitData = {
        ...values,
        configuration_id: parseInt(values.configuration_id),
        parent_vehicle_id: values.parent_vehicle_id || null,
        allow_dual_operator: !!values.allow_dual_operator
      };
      updateVehicle(submitData);
    },
  });

  // Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue !== 1) { // Si no estamos en el tab de sensores
      setRotationMode(false);
      setSelectedPositions([]);
    }
  };

  const handleUnassignSensor = (assignmentId) => {
    setConfirmDialog({ open: true, sensorId: assignmentId });
  };

  const confirmUnassign = () => {
    if (confirmDialog.sensorId) {
      unassignSensor(confirmDialog.sensorId);
    }
    setConfirmDialog({ open: false, sensorId: null });
  };

  const handlePositionClick = (position) => {
    if (editMode && !rotationMode) {
      setSensorDialog({ open: true, position });
    } else if (rotationMode) {
      handleRotationPositionClick(position);
    }
  };

  const handleAssignSensor = (sensorId, position) => {
    assignSensor({ sensor_id: sensorId, position });
  };

  const handleRegisterSensor = async (payload) => {
    const sensorsToCreate = Array.isArray(payload?.sensors) ? payload.sensors : [payload];

    setRegisteringSensors(true);
    try {
      for (const entry of sensorsToCreate) {
        await api.sensors.create({ sensor_id: entry.sensor_id });
      }

      showSuccess(
        sensorsToCreate.length === 1
          ? t('sensors.actions.sensorCreated')
          : t('hardcoded.components_vehicles_vehicledetail.length_sensores_registrados_exitosamente', { length: sensorsToCreate.length })
      );
      setRegisterSensorDialog(false);
      await Promise.all([refetchVehicle(), refetchSensors()]);
      onSensorUnassigned();
      return { success: true, data: sensorsToCreate };
    } catch (error) {
      showError(error.message || t('hardcoded.components_vehicles_vehicledetail.error_al_registrar_sensor'));
      return { error: true, message: error.message };
    } finally {
      setRegisteringSensors(false);
    }
  };

  // Identidad estable: si fuera una funcion inline, el panel de operadores
  // recargaria en bucle (su efecto depende de este callback).
  const handleOperatorAssignments = useCallback((list) => {
    setOperatorCount(Array.isArray(list) ? list.length : 0);
  }, []);

  const handleDeleteVehicle = () => {
    setDeleteVehicleDialog(true);
  };

  const handleRotationPositionClick = (position) => {
    if (!rotationMode) return;

    const sensor = currentVehicle.vehicle_sensors?.find(vs => vs.position === position);
    if (!sensor) return;

    if (selectedPositions.includes(position)) {
      setSelectedPositions(selectedPositions.filter(p => p !== position));
    } else if (selectedPositions.length < 2) {
      const newSelected = [...selectedPositions, position];
      setSelectedPositions(newSelected);

      if (newSelected.length === 2) {
        setTimeout(() => setRotationConfirmDialog(true), 300);
      }
    }
  };

  const executeRotation = async () => {
    try {
      const [pos1, pos2] = selectedPositions;

      // Atomic rotation: a single backend call swaps both assignments inside
      // one DB transaction. Replaces the previous 4-call sequence which could
      // race with other clients and leave the vehicle partially rotated.
      const response = await api.vehicles.rotateSensors(vehicleId, pos1, pos2);

      if (response?.error) {
        const msg = response.message || response.error || t('hardcoded.components_vehicles_vehicledetail.error_desconocido');
        showError(t('vehicleDetail.rotation.error', 'Error al rotar sensores: ') + msg);
        setRotationConfirmDialog(false);
        return;
      }

      showSuccess(t('vehicleDetail.rotation.success', 'Sensores rotados exitosamente'));

      setSelectedPositions([]);
      setRotationMode(false);
      setRotationConfirmDialog(false);

      refetchVehicle();
      refetchSensors();
      onSensorUnassigned();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || t('hardcoded.components_vehicles_vehicledetail.error_desconocido');
      showError(t('vehicleDetail.rotation.error', 'Error al rotar sensores: ') + msg);
      setRotationConfirmDialog(false);
    }
  };

  const getAvailableSensors = () => {
    if (!availableSensorsData || !allVehicles) return availableSensorsData || [];

    const assignedSensorIds = new Set();

    allVehicles.forEach(v => {
      if (v.vehicle_sensors && Array.isArray(v.vehicle_sensors)) {
        v.vehicle_sensors.forEach(vs => {
          if (vs.sensor_id) {
            assignedSensorIds.add(vs.sensor_id);
          }
        });
      }
    });

    return availableSensorsData.filter(sensor => !assignedSensorIds.has(sensor.sensor_id));
  };

  const getAvailableParentVehicles = () => {
    if (!allVehicles || !currentVehicle) return [];

    return allVehicles.filter(v => {
      if (v.id === currentVehicle.id) return false;
      if (v.parent_vehicle_id) return false;
      if (v.parent_vehicle_id === currentVehicle.id) return false;
      return true;
    });
  };

  if (!open || !currentVehicle) return null;

  const canDeleteVehicle = hasPermission('admin') || hasPermission('superadmin');

  // Responsive Dialog
  const DialogComponent = isMobile ? Drawer : Dialog;
  const dialogProps = isMobile ? {
    anchor: 'bottom',
    PaperProps: {
      sx: {
        height: '95vh',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
      }
    }
  } : {
    maxWidth: 'lg',
    fullWidth: true,
    PaperProps: {
      sx: {
        borderRadius: 3,
        height: '90vh',
        maxHeight: '90vh',
      }
    }
  };

  return (
    <DialogComponent
      open={open}
      onClose={onClose}
      {...dialogProps}
      PaperProps={{
        ...(dialogProps.PaperProps || {}),
        className: ['aps-print-area', dialogProps.PaperProps?.className].filter(Boolean).join(' '),
      }}
    >
      {/* Encabezado que solo aparece en papel. */}
      <Box className="aps-print-header">
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Detalle de unidad — Arsenal APS
        </Typography>
        <Typography variant="body2">
          {ensureString(currentVehicle.display_name)}
          {currentVehicle.company_name ? ` · ${ensureString(currentVehicle.company_name)}` : ''}
        </Typography>
        <Typography variant="caption">Impreso: {printTimestamp()}</Typography>
      </Box>

      {/* Header */}
      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        p: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 40, sm: 56 },
                height: { xs: 40, sm: 56 },
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <VehicleIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 28 } }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} noWrap>
                  {ensureString(currentVehicle.display_name)}
                </Typography>
                {editMode && (
                  <Chip size="small" color="warning" label={t('hardcoded.components_vehicles_vehicledetail.editando')} sx={{ height: 22, fontWeight: 700 }} />
                )}
              </Stack>
              <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" noWrap>
                {ensureString(currentVehicle.make)} {ensureString(currentVehicle.model)} {ensureString(currentVehicle.year)}
              </Typography>
              {/* Chips de estado: sensores, receptores, operadores y doble operador */}
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip size="small" variant="outlined" icon={<SensorIcon sx={{ fontSize: 15 }} />}
                  label={t('hardcoded.components_vehicles_vehicledetail.currentvehicles_sensores', { currentVehicles: currentVehicle.sensor_count ?? 0 })} sx={{ height: 22 }} />
                <Chip size="small" variant="outlined" icon={<ReceiverIcon sx={{ fontSize: 15 }} />}
                  label={t('hardcoded.components_vehicles_vehicledetail.currentvehicler_receptores', { currentVehicler: currentVehicle.receiver_count ?? 0 })} sx={{ height: 22 }} />
                <Chip size="small" variant="outlined" icon={<OperatorIcon sx={{ fontSize: 15 }} />}
                  label={`${operatorCount ?? 0}/${currentVehicle.max_operators ?? (currentVehicle.allow_dual_operator ? 2 : 1)} operadores`}
                  sx={{ height: 22 }} />
                <Chip size="small"
                  color={currentVehicle.allow_dual_operator ? 'primary' : 'default'}
                  variant={currentVehicle.allow_dual_operator ? 'filled' : 'outlined'}
                  label={currentVehicle.allow_dual_operator ? t('hardcoded.components_vehicles_vehicledetail.doble_operador') : t('hardcoded.components_vehicles_vehicledetail.operador_unico')}
                  sx={{ height: 22 }} />
              </Stack>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
            {/* En modo edición, Guardar/Cancelar viven en la barra inferior fija.
                Aquí, en modo lectura, mostramos botones claros con texto. */}
            {/* Imprimir: solo en el detalle de la unidad (no en toda la app). */}
            {!editMode && (
              <Tooltip title={t('hardcoded.components_fleet.imprimir')}>
                <IconButton
                  className="aps-no-print"
                  onClick={printSection}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.10) }}
                >
                  <PrintIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
              </Tooltip>
            )}
            {!editMode && !isMobile && (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                  sx={{ fontWeight: 600 }}
                >
                  {t('common.edit', 'Editar')}
                </Button>
                {canDeleteVehicle && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteVehicle}
                  >
                    {t('vehicles.actions.deleteVehicle', 'Eliminar')}
                  </Button>
                )}
              </>
            )}
            {!editMode && isMobile && (
              <>
                <Tooltip title={t('common.edit', 'Editar')}>
                  <IconButton onClick={() => setEditMode(true)} size="small"
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12) }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canDeleteVehicle && (
                  <Tooltip title={t('vehicles.actions.deleteVehicle', 'Eliminar')}>
                    <IconButton onClick={handleDeleteVehicle} size="small"
                      sx={{ bgcolor: alpha(theme.palette.error.main, 0.12) }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            <IconButton
              onClick={onClose}
              size={isMobile ? "small" : "medium"}
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.2) }
              }}
            >
              <CloseIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 60 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 600,
              px: { xs: 2, sm: 3 }
            }
          }}
        >
          <Tab label={t('vehicleDetail.tabs.overview', 'Resumen')} />
          <Tab label={t('vehicleDetail.tabs.sensors', 'Sensores')} />
          <Tab label={t('vehicleDetail.tabs.connectivity', 'Conectividad')} />
          <Tab label={t('alarms.profiles.title', 'Perfiles de Alerta')} />
          <Tab label={t('hardcoded.components_common_sidebar.operadores')} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'auto',
        height: 0
      }}>
        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <VehicleOverviewTab
            currentVehicle={currentVehicle}
            formik={formik}
            editMode={editMode}
            vehicleConfigs={vehicleConfigs}
            availableParentVehicles={getAvailableParentVehicles()}
            onPositionClick={handlePositionClick}
            t={t}
          />
        </TabPanel>

        {/* Sensors Tab */}
        <TabPanel value={tabValue} index={1}>
          <VehicleSensorsTab
            currentVehicle={currentVehicle}
            editMode={editMode}
            rotationMode={rotationMode}
            selectedPositions={selectedPositions}
            onUnassignSensor={handleUnassignSensor}
            onPositionClick={handlePositionClick}
            onRegisterSensor={() => setRegisterSensorDialog(true)}
            onToggleRotationMode={() => {
              setRotationMode(!rotationMode);
              setSelectedPositions([]);
            }}
            onClearSelection={() => setSelectedPositions([])}
            unassigning={unassigning}
            t={t}
          />
        </TabPanel>

        {/* Connectivity Tab */}
        <TabPanel value={tabValue} index={2}>
          <VehicleConnectivityTab
            vehicle={currentVehicle}
            t={t}
            onRefresh={refetchVehicle}
          />
        </TabPanel>

        {/* Alarm Profiles Tab */}
        <TabPanel value={tabValue} index={3}>
          <AlarmProfilesTab
            vehicle={currentVehicle}
            t={t}
            onRefresh={refetchVehicle}
          />
        </TabPanel>

        {/* Operadores Tab — asignar/quitar operadores (límite según el vehículo) */}
        <TabPanel value={tabValue} index={4}>
          <VehicleOperatorsPanel
            vehicleId={currentVehicle?.id}
            maxOperators={currentVehicle?.max_operators ?? (currentVehicle?.allow_dual_operator ? 2 : 1)}
            onAssignmentsChanged={handleOperatorAssignments}
          />
        </TabPanel>
      </Box>

      {/* Barra de acciones fija */}
      <Box sx={{
        p: { xs: 1.5, sm: 2 },
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: editMode ? alpha(theme.palette.warning.main, 0.08) : alpha(theme.palette.grey[50], 0.8)
      }}>
        {editMode ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 600 }}>
              {formik.dirty ? t('hardcoded.components_vehicles_vehicledetail.tienes_cambios_sin_guardar') : t('hardcoded.components_vehicles_vehicledetail.modo_edicion')}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                onClick={() => { setEditMode(false); formik.resetForm(); }}
                variant="outlined"
                color="inherit"
                startIcon={<CancelIcon />}
                disabled={updating}
              >
                {t('common.cancel', 'Cancelar')}
              </Button>
              <Button
                onClick={formik.handleSubmit}
                variant="contained"
                color="success"
                startIcon={updating ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={updating}
                sx={{ fontWeight: 700, minWidth: 160 }}
              >
                {t('common.save', 'Guardar cambios')}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={onClose}
              variant="contained"
              fullWidth={isMobile}
              size="large"
              sx={{ px: 4, py: 1.25, fontSize: '1rem', fontWeight: 600 }}
            >
              {t('vehicleDetail.close', 'Cerrar')}
            </Button>
          </Box>
        )}
      </Box>

      {/* Sensor Assignment Dialog */}
      <SensorAssignmentDialog
        open={sensorDialog.open}
        onClose={() => setSensorDialog({ open: false, position: null })}
        position={sensorDialog.position}
        availableSensors={getAvailableSensors()}
        onAssign={handleAssignSensor}
        assigning={assigning}
        t={t}
      />

      {/* Sensor Registration Dialog */}
      <SensorRegistrationDialog
        open={registerSensorDialog}
        onClose={() => setRegisterSensorDialog(false)}
        onSubmit={handleRegisterSensor}
        loading={registeringSensors}
      />

      {/* Confirmation Dialog for Unassigning */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, sensorId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: alpha(theme.palette.error.main, 0.05),
          color: 'error.main',
          fontWeight: 700
        }}>
          {t('vehicleDetail.sensors.confirmUnassign', 'Confirmar Desasignación')}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <DialogContentText>
            {t('vehicleDetail.sensors.unassignMessage', '¿Estás seguro?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDialog({ open: false, sensorId: null })} color="inherit" variant="outlined">
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={confirmUnassign}
            color="error"
            variant="contained"
            disabled={unassigning}
          >
            {unassigning ? t('vehicleDetail.sensors.unassigning', 'Desasignando...') : t('vehicleDetail.sensors.unassign', 'Desasignar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Vehicle Dialog */}
      <Dialog
        open={deleteVehicleDialog}
        onClose={() => setDeleteVehicleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: alpha(theme.palette.error.main, 0.05),
          color: 'error.main',
          fontWeight: 700
        }}>
          {t('vehicles.actions.confirmDelete', 'Confirmar Eliminación')}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <DialogContentText sx={{ mb: 2 }}>
            {t('vehicles.actions.deleteMessage', '¿Estás seguro?')}
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {currentVehicle.display_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentVehicle.make} {currentVehicle.model}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteVehicleDialog(false)} color="inherit" variant="outlined">
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={() => deleteVehicle()}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('common.deleting', 'Eliminando...') : t('common.delete', 'Eliminar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rotation Confirmation Dialog */}
      <Dialog
        open={rotationConfirmDialog}
        onClose={() => setRotationConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          color: 'primary.main',
          fontWeight: 700
        }}>
          {t('vehicleDetail.rotation.confirmTitle', 'Confirmar Rotación')}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {selectedPositions.length === 2 && (
            <>
              <Typography variant="body1" gutterBottom>
                {t('vehicleDetail.rotation.confirmMessage', '¿Deseas intercambiar estos sensores?')}
              </Typography>

              <RotationPreview
                vehicle={currentVehicle}
                selectedPositions={selectedPositions}
                t={t}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setRotationConfirmDialog(false)}
            color="inherit"
            variant="outlined"
            fullWidth={isMobile}
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={executeRotation}
            color="primary"
            variant="contained"
            startIcon={<SwapIcon />}
            fullWidth={isMobile}
          >
            {t('vehicleDetail.rotation.execute', 'Rotar')}
          </Button>
        </DialogActions>
      </Dialog>
    </DialogComponent>
  );
};

export default VehicleDetail;